import React from 'react';

interface GovernanceHelpProps {
  subTab: string;
}

/**
 * Governance Help Content - Approvals, Releases, Audit, Policies, Auto-Actions, Costs
 */
const GovernanceHelp: React.FC<GovernanceHelpProps> = ({ subTab }) => {
  switch (subTab) {
    case 'environments':
      return <EnvironmentsHelp />;
    case 'releases':
      return <ReleasesHelp />;
    case 'audit':
      return <AuditHelp />;
    case 'policies':
      return <PoliciesHelp />;
    case 'auto-actions':
      return <AutoActionsHelp />;
    case 'costs':
      return <CostsHelp />;
    default:
      return <EnvironmentsHelp />;
  }
};

const EnvironmentsHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Environments
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Environments define the stages in your prompt promotion chain. By default, every organization starts with Development, Staging, and Production, but you can create custom environments to match your workflow.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Promotion Chain
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
        The promotion chain is determined by the position order of your environments. Prompts can only be promoted forward in the chain:
      </p>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Each environment has a <span className="font-medium">position</span> that defines its order</p>
        <p>&#8226; Reorder environments to change the promotion flow</p>
        <p>&#8226; Mark an environment as <span className="font-medium">terminal</span> to indicate production-like stages</p>
        <p>&#8226; Promotion policies are configured separately under the Policies tab</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Managing Environments
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Create</span>: Add new environments with a unique name and color</p>
        <p>&#8226; <span className="font-medium">Reorder</span>: Use the arrow buttons to change the promotion order</p>
        <p>&#8226; <span className="font-medium">Edit</span>: Update display name, description, color, or terminal status</p>
        <p>&#8226; <span className="font-medium">Delete</span>: Remove environments that have no prompts assigned</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Bedrock Sync
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Environments are an Aivilo concept — AWS Bedrock does not have native environments. When a prompt is synced to Bedrock, it is named using the convention <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{'prompt-name-environment'}</code> (e.g., <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">my-prompt-production</code>).
      </p>
    </div>

  </>
);

const ReleasesHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Releases
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Track the deployment history of your prompts. Releases show when changes were promoted to each environment and by whom.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Release Information
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Version</span>: The prompt version deployed</p>
        <p>&#8226; <span className="font-medium">Environment</span>: Target deployment environment</p>
        <p>&#8226; <span className="font-medium">Timestamp</span>: When the release occurred</p>
        <p>&#8226; <span className="font-medium">Released By</span>: User who approved the release</p>
        <p>&#8226; <span className="font-medium">Status</span>: Active, rolled back, or superseded</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Rollbacks
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        If issues arise, you can roll back to a previous version directly from the releases view. Rollbacks create a new release entry for audit purposes.
      </p>
    </div>

  </>
);

const AuditHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Audit Log
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        The Audit Log provides a complete record of all actions taken in GenAIOps. Use it for compliance, debugging, and understanding who made what changes.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Tracked Events
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Prompt creates, updates, and deletions</p>
        <p>&#8226; Promotion requests and approvals</p>
        <p>&#8226; Dataset modifications</p>
        <p>&#8226; Evaluation runs</p>
        <p>&#8226; Policy changes</p>
        <p>&#8226; User access and permission changes</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Filtering Events
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Filter by date range, user, action type, or resource to find specific events. Export logs for compliance reporting.
      </p>
    </div>

  </>
);

const PoliciesHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Promotion Policies
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Promotion policies define the rules and requirements for moving prompts between environments. Configure approval requirements, evaluation gates, and more.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Policy Settings
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Required Approvers</span>: Number of approvals needed</p>
        <p>&#8226; <span className="font-medium">Evaluation Gates</span>: Minimum test scores required</p>
        <p>&#8226; <span className="font-medium">Auto-approve</span>: Skip approval for certain conditions</p>
        <p>&#8226; <span className="font-medium">Expiration</span>: Time limit for pending approvals</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Creating Policies
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Click <span className="font-semibold">Create Policy</span> to define a new policy. Assign policies to specific prompts or apply globally to all promotions.
      </p>
    </div>

  </>
);

const AutoActionsHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Auto-Actions
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Auto-Actions automate responses to events in your AI operations. Configure rules to automatically handle common scenarios without manual intervention.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Common Actions
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Auto-rollback</span>: Revert if error rate exceeds threshold</p>
        <p>&#8226; <span className="font-medium">Auto-approve</span>: Approve promotions meeting criteria</p>
        <p>&#8226; <span className="font-medium">Notifications</span>: Alert on specific conditions</p>
        <p>&#8226; <span className="font-medium">Webhooks</span>: Trigger external systems</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Creating Rules
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Define trigger conditions and actions. Test rules before enabling to ensure they behave as expected.
      </p>
    </div>

  </>
);

const CostsHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Cost Analytics
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Track and analyze your AI operations costs. Monitor spending by model, prompt, application, and time period.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Cost Breakdown
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">By Model</span>: Spending per LLM model</p>
        <p>&#8226; <span className="font-medium">By Prompt</span>: Cost per prompt template</p>
        <p>&#8226; <span className="font-medium">By Application</span>: Spending per app or service</p>
        <p>&#8226; <span className="font-medium">By Environment</span>: Production vs. development costs</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Cost Forecasting
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        View projected costs based on current usage trends. Set budget alerts to avoid unexpected charges.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Optimization Tips
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Identify high-cost prompts for optimization</p>
        <p>&#8226; Compare model costs for similar quality</p>
        <p>&#8226; Monitor token usage efficiency</p>
      </div>
    </div>

  </>
);

export default GovernanceHelp;
